import { useMutation } from '@tanstack/react-query';
import { quotasApi, type QuotaImportDiff, type QuotaImportScope } from '../api/quotasApi';

interface QuotaImportUpload {
  file: File;
  scope: QuotaImportScope;
}

interface QuotaImportMutationOptions<TResult> {
  onSuccess?: (result: TResult) => void;
  onError?: (error: Error) => void;
}

/** Previews the grid. Writes nothing, so it invalidates nothing. */
export const useValidateQuotaCSV = (options: QuotaImportMutationOptions<QuotaImportDiff> = {}) =>
  useMutation({
    mutationFn: ({ file, scope }: QuotaImportUpload) => quotasApi.validateImport(file, scope),
    onSuccess: options.onSuccess,
    onError: (error: Error) => {
      console.error('Error validating the quota CSV:', error);
      options.onError?.(error);
    },
  });

/**
 * Applies the grid. Nothing is invalidated here: the quota list is plain page state loaded by
 * `loadQuotas`, not a React Query cache — the caller refreshes it through `onSuccess`.
 */
export const useApplyQuotaCSV = (
  options: QuotaImportMutationOptions<{ diff: QuotaImportDiff; applied: boolean }> = {}
) =>
  useMutation({
    mutationFn: ({ file, scope }: QuotaImportUpload) => quotasApi.applyImport(file, scope),
    onSuccess: options.onSuccess,
    onError: (error: Error) => {
      console.error('Error importing the quota CSV:', error);
      options.onError?.(error);
    },
  });
